import {
	getDeclarations,
	updateDeclarations,
} from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { DataDeclarationTct, KycWorkflowSettingResponseDto } from '@dealstack/shared/lib/generated';
import { isKycRejected } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/utils.ts';
import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { yupResolver } from '@hookform/resolvers/yup';
import {
	SAVE_FAILED,
	SAVE_INVALID,
	SAVE_OK,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import type { WizardSaveResult } from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry.types';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';

export enum SOURCE_OF_FUNDS_TYPE {
	SALARY = 'SOURCE_OF_FUNDS_SALARY',
	INHERITANCE = 'SOURCE_OF_FUNDS_INHERITANCE',
	INVESTMENT_INCOME = 'SOURCE_OF_FUNDS_INVESTMENT_INCOME',
	PERSONAL_SAVINGS = 'SOURCE_OF_FUNDS_PERSONAL_SAVINGS',
	OTHER = 'SOURCE_OF_FUNDS_OTHER',
}

export enum DECLARATIONS_FORM {
	BENEFICIAL_OWNERSHIP = 'BENEFICIAL_OWNERSHIP',
	SOURCE_OF_FUNDS = 'SOURCE_OF_FUNDS',
	SOURCE_OF_FUNDS_OTHER_DETAILS = 'SOURCE_OF_FUNDS_OTHER_DETAILS',
	IS_POLITICALLY_EXPOSED_PERSON = 'POLITICALLY_EXPOSED_PERSON',
	PEP_DETAILS = 'PEP_DETAILS',
}

export const FUNDS_CHECKBOXES = [
	{
		name: SOURCE_OF_FUNDS_TYPE.SALARY,
		label: 'investment.join.kyc.declarations.funds.label_1',
		testId: 'SOURCE_OF_FUNDS_SALARY_CHECKBOX' as const,
	},
	{
		name: SOURCE_OF_FUNDS_TYPE.INHERITANCE,
		label: 'investment.join.kyc.declarations.funds.label_2',
		testId: 'SOURCE_OF_FUNDS_INHERITANCE_CHECKBOX' as const,
	},
	{
		name: SOURCE_OF_FUNDS_TYPE.INVESTMENT_INCOME,
		label: 'investment.join.kyc.declarations.funds.label_3',
		testId: 'SOURCE_OF_FUNDS_INVESTMENT_INCOME_CHECKBOX' as const,
	},
	{
		name: SOURCE_OF_FUNDS_TYPE.PERSONAL_SAVINGS,
		label: 'investment.join.kyc.declarations.funds.label_4',
		testId: 'SOURCE_OF_FUNDS_PERSONAL_SAVINGS_CHECKBOX' as const,
	},
	{
		name: SOURCE_OF_FUNDS_TYPE.OTHER,
		label: 'investment.join.kyc.declarations.funds.label_5',
		testId: 'SOURCE_OF_FUNDS_OTHER_CHECKBOX' as const,
	},
];

export interface DeclarationsFormValues {
	[DECLARATIONS_FORM.BENEFICIAL_OWNERSHIP]: boolean;
	[DECLARATIONS_FORM.SOURCE_OF_FUNDS]: string[];
	[DECLARATIONS_FORM.SOURCE_OF_FUNDS_OTHER_DETAILS]: string;
	[DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON]: boolean | undefined;
	[DECLARATIONS_FORM.PEP_DETAILS]: string;
}

interface Props {
	setIsContinueDisabled: (isContinueDisabled: boolean) => void;
	setIsLoading: (isLoading: boolean) => void;
	kycWorkflowId: string;
	kycSetting: KycWorkflowSettingResponseDto | undefined;
}

export const useInvestmentManageDeclarationsController = ({
	setIsContinueDisabled,
	setIsLoading,
	kycWorkflowId,
	kycSetting,
}: Props) => {
	const tInvestor = useTranslationInvestor();

	const isBeneficialOwnershipRequired = kycSetting?.isBeneficialOwnershipRequired ?? true;
	const isSourceOfFundsRequired = kycSetting?.isSourceOfFundsRequired ?? true;
	const isPepRequired = kycSetting?.isPepRequired ?? true;
	const [isPEPModalVisible, setPEPModalVisible] = useState(false);

	const declarationsQuery = useQuery({
		queryKey: [API_PATHS.SHAREHOLDER_KYC_DECLARATIONS_GET, kycWorkflowId],
		queryFn: () => getDeclarations(kycWorkflowId),
		select: (data) => data.payload,
	});
	const updateDeclarationsMutation = useMutation({
		mutationFn: (variables: Parameters<typeof updateDeclarations>[0]) =>
			updateDeclarations(variables),
	});

	const commonYup = useCommonYupValidation();

	const declarationsForm = useForm<DeclarationsFormValues>({
		mode: 'all',
		reValidateMode: 'onChange',
		resolver: yupResolver(
			Yup.object().shape({
				[DECLARATIONS_FORM.BENEFICIAL_OWNERSHIP]: isBeneficialOwnershipRequired
					? commonYup.requiredConsent
					: Yup.boolean().notRequired(),
				[DECLARATIONS_FORM.SOURCE_OF_FUNDS]: isSourceOfFundsRequired
					? Yup.array()
							.min(1, 'Please check at least one checkbox')
							.required('Please check at least one checkbox')
					: Yup.array().notRequired(),
				[DECLARATIONS_FORM.SOURCE_OF_FUNDS_OTHER_DETAILS]: Yup.string()
					.when([DECLARATIONS_FORM.SOURCE_OF_FUNDS], {
						is: (value: string[]) =>
							isSourceOfFundsRequired && value?.includes(SOURCE_OF_FUNDS_TYPE.OTHER),
						then: () => commonYup.string.required.nullable(),
						otherwise: () => commonYup.string.optional,
					})
					.nullable(),
				[DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON]: isPepRequired
					? Yup.boolean().required('Please check one checkbox')
					: Yup.boolean().notRequired(),
				[DECLARATIONS_FORM.PEP_DETAILS]: Yup.string()
					.when([DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON], {
						is: (value: boolean) => isPepRequired && value,
						then: () => commonYup.string.required,
					})
					.nullable(),
			})
		),
		defaultValues: {
			[DECLARATIONS_FORM.SOURCE_OF_FUNDS]: [],
		},
	});

	const parseResponseToFormValues = (payload: DataDeclarationTct[]) => {
		return payload?.reduce((acc, declaration) => {
			if (declaration.type.includes(DECLARATIONS_FORM.BENEFICIAL_OWNERSHIP)) {
				return {
					...acc,
					[DECLARATIONS_FORM.BENEFICIAL_OWNERSHIP]: declaration.checked,
				};
			}

			if (
				declaration.type.includes(DECLARATIONS_FORM.SOURCE_OF_FUNDS) &&
				declaration.checked
			) {
				if (declaration.type.includes(SOURCE_OF_FUNDS_TYPE.OTHER)) {
					return {
						...acc,
						[DECLARATIONS_FORM.SOURCE_OF_FUNDS]: [
							...(acc[DECLARATIONS_FORM.SOURCE_OF_FUNDS] || []),
							declaration.type,
						],
						[DECLARATIONS_FORM.SOURCE_OF_FUNDS_OTHER_DETAILS]: declaration.description,
					};
				}
				return {
					...acc,
					[DECLARATIONS_FORM.SOURCE_OF_FUNDS]: [
						...(acc[DECLARATIONS_FORM.SOURCE_OF_FUNDS] || []),
						declaration.type,
					],
				};
			}

			if (declaration.type.includes(DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON)) {
				return {
					...acc,
					[DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON]: declaration.checked,
					[DECLARATIONS_FORM.PEP_DETAILS]: declaration.description,
				};
			}

			return acc;
		}, {} as DeclarationsFormValues);
	};

	useEffect(() => {
		const isPep = declarationsForm.getValues(DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON);
		if (isPep) {
			return;
		}

		declarationsForm.clearErrors(DECLARATIONS_FORM.PEP_DETAILS);
	}, [declarationsForm.watch(DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON)]);

	useEffect(() => {
		const isFundsOtherChecked = declarationsForm
			.getValues(DECLARATIONS_FORM.SOURCE_OF_FUNDS)
			.includes(SOURCE_OF_FUNDS_TYPE.OTHER);
		if (isFundsOtherChecked) {
			return;
		}

		declarationsForm.clearErrors(DECLARATIONS_FORM.SOURCE_OF_FUNDS_OTHER_DETAILS);
	}, [declarationsForm.watch(DECLARATIONS_FORM.SOURCE_OF_FUNDS)]);

	const declarationsPayload = declarationsQuery.data;

	useEffect(() => {
		if (!declarationsPayload) {
			return;
		}
		declarationsForm.reset(parseResponseToFormValues(declarationsPayload));
	}, [declarationsPayload]);

	const isDeclarationsRejected = !!declarationsPayload?.some((declaration) =>
		isKycRejected(declaration.status)
	);
	const rejectionComment = declarationsPayload?.[0]?.rejectionComment;

	useEffect(() => {
		setIsContinueDisabled(
			!!Object.entries(declarationsForm.formState.errors).length ||
				(!declarationsForm.formState.isDirty && isDeclarationsRejected)
		);
	}, [declarationsForm.formState, isDeclarationsRejected]);

	const onSubmit = (): Promise<WizardSaveResult> =>
		new Promise((resolve) => {
			setIsLoading(true);

			const formValues = declarationsForm.getValues();

			/**
			 * D.slaby MIP-776 07.07.2023
			 * When declaration is in fact the same as previous one, don't generate new
			 * Declaration entry
			 */
			if (declarationsPayload) {
				const responseToForm = parseResponseToFormValues(declarationsPayload);
				const isChanged = JSON.stringify(responseToForm) === JSON.stringify(formValues);
				if (isChanged) {
					showToastV2({
						title: 'Data saved successfully!',
					});
					resolve(SAVE_OK);
					setIsLoading(false);
					return;
				}
			}

			const getIsChecked = (type: SOURCE_OF_FUNDS_TYPE) =>
				formValues[DECLARATIONS_FORM.SOURCE_OF_FUNDS].includes(type);

			updateDeclarationsMutation.mutate(
				{
					kycWorkflowId,
					beneficialOwnership: formValues[DECLARATIONS_FORM.BENEFICIAL_OWNERSHIP],
					sourceOfFundsSalary: getIsChecked(SOURCE_OF_FUNDS_TYPE.SALARY),
					sourceOfFundsInheritence: getIsChecked(SOURCE_OF_FUNDS_TYPE.INHERITANCE),
					sourceOfFundsInvestmentIncome: getIsChecked(
						SOURCE_OF_FUNDS_TYPE.INVESTMENT_INCOME
					),
					sourceOfFundsPersonalSavings: getIsChecked(
						SOURCE_OF_FUNDS_TYPE.PERSONAL_SAVINGS
					),
					sourceOfFundsOther: getIsChecked(SOURCE_OF_FUNDS_TYPE.OTHER),
					sourceOfFundsOtherDescription:
						formValues[DECLARATIONS_FORM.SOURCE_OF_FUNDS_OTHER_DETAILS],
					politicallyExposedPerson:
						!!formValues[DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON],
					politicallyExposedPersonDescription: formValues[DECLARATIONS_FORM.PEP_DETAILS],
				},
				{
					onSuccess: () => {
						void declarationsQuery.refetch();
						showToastV2({
							title: 'Data saved successfully!',
						});
						resolve(SAVE_OK);
					},
					onError: () => {
						resolve(SAVE_FAILED);
					},
					onSettled: () => {
						setIsLoading(false);
					},
				}
			);
		});

	const onInvalid = () => {
		showToastV2({
			title: tInvestor('investment.join.kyc.declarations.fill_all_fields'),
			state: 'error',
		});
	};

	useEffect(() => {
		const showSourceOfFundsTextarea = !!declarationsForm.watch(
			DECLARATIONS_FORM.SOURCE_OF_FUNDS
		)?.[SOURCE_OF_FUNDS_TYPE.OTHER];
		if (!showSourceOfFundsTextarea) {
			declarationsForm.clearErrors(DECLARATIONS_FORM.SOURCE_OF_FUNDS_OTHER_DETAILS);
		}
	}, [declarationsForm.watch(DECLARATIONS_FORM.SOURCE_OF_FUNDS)?.[SOURCE_OF_FUNDS_TYPE.OTHER]]);

	useWizardStep(async (intent) => {
		if (intent !== 'next') {
			return SAVE_OK;
		}
		const isValid = await declarationsForm.trigger();
		if (!isValid) {
			onInvalid();
			return SAVE_INVALID;
		}
		return onSubmit();
	});

	return {
		isPEPModalVisible,
		setPEPModalVisible,
		declarationsForm,
		isDeclarationsRejected,
		rejectionComment,
		isBeneficialOwnershipRequired,
		isSourceOfFundsRequired,
		isPepRequired,
	};
};
